import { cn } from '@bazaar-marketplace/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AddressFormField } from '~/components/address-form-field';
import { Ball } from '~/components/collection-card/assets';
import { ConfirmationContent } from '~/components/confirmation-content';
import { StepList } from '~/components/step-list';
import { TransactionStatusDisplay } from '~/components/transaction-status-display';
import { Button } from '~/components/ui';
import { Form } from '~/components/ui/form';
import { TGS_STICKERS } from '~/constants/tgs-stickers';
import { useUserEventsStream } from '~/hooks/use-user-events-stream';
import { useNftControllerWithdrawNft, useUsersControllerGetProfile } from '~/lib/api';
import type { PackResponseDto } from '~/lib/api';
import { NftFeeText } from '~/module/nft/nft-fee-text';
import { zTonAddressInput } from '~/module/ton/address';
import { AxiosError } from 'axios';

type WithdrawStateCode = 'CONFIRM' | 'ENTER_ADDRESS' | 'ERROR' | 'PENDING';

const withdrawSchema = z.object({
  address: zTonAddressInput,
});

type WithdrawFormData = z.infer<typeof withdrawSchema>;

interface WithdrawNftFlowProps {
  pack: PackResponseDto;
  onSuccess?: () => void;
}

export function WithdrawNftFlow(props: WithdrawNftFlowProps) {
  const queryClient = useQueryClient();
  const transferFee = '0.1';
  const t = useTranslations('my-collection.nft');
  const [state, setState] = useState<WithdrawStateCode>('CONFIRM');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasWithdrawCompleted, setHasWithdrawCompleted] = useState(false);
  const { data: user } = useUsersControllerGetProfile();

  const withdrawMutation = useNftControllerWithdrawNft({
    mutation: {
      onSuccess: () => {
        setState('PENDING');
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          if (error.status === 400) {
            setErrorMessage((error.response?.data as { message: string }).message);
            setState('ERROR');
            return;
          }
          if (error.status === 404) {
            setErrorMessage(t('withdraw.error-message'));
            setState('ERROR');
            return;
          }
          if (error.status === 429) {
            setErrorMessage(t('withdraw.error-message'));
            setState('ERROR');
            return;
          }
        }
      },
    },
  });

  const form = useForm<WithdrawFormData>({
    resolver: zodResolver(withdrawSchema),
    defaultValues: {
      address: '',
    },
  });

  // Listen to user events for withdraw progress
  useUserEventsStream({
    userId: user?.data.id ?? '',
    onEvent: (event) => {
      if (event.type === 'NFT_AUDIT' && event.data.packId === props.pack.id) {
        const isCompleted = event.data.action === 'WITHDRAW_COMPLETED';
        if (isCompleted) {
          setHasWithdrawCompleted(true);
          // we must not invalidate the query here, as that would unmount the flow
          return;
        }

        const isFailed = event.data.action === 'WITHDRAW_FAILED';
        if (isFailed) {
          setErrorMessage(t('withdraw.error-message'));
          setState('ERROR');
          return;
        }
      }
    },
  });

  const mintSteps = [
    { stepNumber: 1, description: t('mint.steps.step-1') },
    { stepNumber: 2, description: t('mint.steps.step-2') },
    { stepNumber: 3, description: t('mint.steps.step-3') },
    { stepNumber: 4, description: t('mint.steps.step-4') },
  ];

  if (hasWithdrawCompleted) {
    return (
      <TransactionStatusDisplay
        buttonText={t('actions.close')}
        message={t('withdraw.completed-message')}
        stickerSrc={TGS_STICKERS.HAPPY}
        title={t('withdraw.completed-title')}
        onButtonClick={() => {
          void queryClient.invalidateQueries();
          props.onSuccess?.();
        }}
      />
    );
  }

  if (state === 'ERROR') {
    return (
      <TransactionStatusDisplay
        buttonText={t('actions.close')}
        message={errorMessage}
        stickerSrc={TGS_STICKERS.VOMIT}
        title={t('withdraw.error-title')}
        onButtonClick={props.onSuccess}
      />
    );
  }

  if (state === 'PENDING') {
    return (
      <TransactionStatusDisplay
        buttonText={t('actions.close')}
        message={t('withdraw.pending-message')}
        stickerSrc={TGS_STICKERS.HAPPY}
        title={t('withdraw.pending-title')}
        onButtonClick={props.onSuccess}
      />
    );
  }

  if (state === 'ENTER_ADDRESS') {
    const addressValue = form.watch('address');
    const isAddressValid =
      addressValue &&
      typeof addressValue === 'string' &&
      addressValue.trim().length > 0 &&
      !form.formState.errors.address;

    return (
      <Form {...form}>
        <form
          className="flex flex-col gap-6 px-2 pt-6 pb-8 h-full"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit((data) => {
              withdrawMutation.mutate({
                packId: props.pack.id,
                data: {
                  packId: props.pack.id,
                  toAddress: data.address,
                },
              });
            })(event);
          }}
        >
          {props.pack.unwrapped ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              alt={props.pack.unwrapped.collectionName}
              className={cn(
                'rounded-xl max-w-[200px] max-h-[100px]',
                props.pack.unwrapped.imagePosition === 'cover' ? 'object-cover' : 'object-contain',
              )}
              src={props.pack.unwrapped.imageUrl}
            />
          ) : (
            <Image alt="Ball" height={112} src={Ball} width={112} />
          )}

          <div className="text-[24px] text-white font-bold">{t('withdraw.title')}</div>

          <AddressFormField label={t('withdraw.address-label')} placeholder={t('withdraw.address-placeholder')} />

          <div className="flex-1" />

          <Button disabled={!isAddressValid} type="submit" variant="rainbow">
            {t('actions.next')}
          </Button>
        </form>
      </Form>
    );
  }

  return (
    <div className="max-h-[90vh] grid grid-rows-[max-content_1fr]">
      <ConfirmationContent
        content={
          <div className="flex flex-col h-full">
            <div className="mb-6 overflow-y-auto">
              <StepList steps={mintSteps} />
            </div>
            <div className="mb-6">
              <NftFeeText fee={transferFee} feeType="TRANSFER" />
            </div>
            <div className="pb-6">
              <Button
                className="w-full"
                variant="rainbow"
                onClick={() => {
                  setState('ENTER_ADDRESS');
                }}
              >
                {t('actions.next')}
              </Button>
            </div>
          </div>
        }
        description={t('withdraw.confirm-description')}
        title={t('withdraw.confirm-title')}
      />
    </div>
  );
}
